import {
  addCalendarDays,
  calendarDateToUtcSeconds,
  calendarDateUtcBounds,
  chicagoCalendarDate,
  isSetupModalItemVisible,
  type FirstRunForks,
  type OrganizationFirstRunState,
  type SetupModalItem,
} from '@ruta/shared';
import { safeLocalStorage } from '@/lib/safeStorage';
import { analytics, PostImportEvents } from '@/services/analytics';
import { updateOrganizationSettings } from '@/lib/api/routes/organizations';

export const importCompletedStorageKey = (orgId: string) => `ruta:import_completed:${orgId}`;
export const firstWeekVisibleStorageKey = (orgId: string) => `ruta:first_week_visible:${orgId}`;
export const emptyStateStorageKey = (orgId: string, serviceId: string) =>
  `ruta:schedule_empty_state:${orgId}:${serviceId}`;
export const setupModalShownStorageKey = (orgId: string, item: SetupModalItem) =>
  `ruta:setup_modal_shown:${orgId}:${item}`;

export function markImportCompletedLocally(orgId: string): void {
  safeLocalStorage.setItem(importCompletedStorageKey(orgId), 'true');
}

export function hasLocalImportCompleted(orgId: string): boolean {
  return safeLocalStorage.getItem(importCompletedStorageKey(orgId)) === 'true';
}

/** Inclusive Typesense window for This Week: UTC midnight of the org-local
 *  calendar day through 23:59:59 of day + 6. Chicago start-of-day is 5–6 hours
 *  later than that midnight, so an evening CT visit (already tomorrow in UTC,
 *  indexed at UTC midnight of the local day) would miss the card (RUT-4418). */
export function nextSevenDayBounds(now = new Date()): { startUnix: number; endUnix: number } {
  const today = chicagoCalendarDate(now.toISOString(), now);
  const last = addCalendarDays(today, 6);
  return {
    startUnix: calendarDateToUtcSeconds(today),
    endUnix: calendarDateUtcBounds(last).end,
  };
}

export function shouldMeasureFirstWeek(input: {
  orgId: string | null | undefined;
  firstRun?: OrganizationFirstRunState | null;
}): boolean {
  if (!input.orgId) return false;
  if (input.firstRun?.firstWeekVisibleAt) return false;
  if (safeLocalStorage.getItem(firstWeekVisibleStorageKey(input.orgId)) === 'true') return false;
  return Boolean(input.firstRun?.importId || hasLocalImportCompleted(input.orgId));
}

export function fireFirstWeekVisible(input: {
  orgId: string;
  role?: string | null;
  visitsShownNext7d: number;
  emptyActiveServices: number;
}): void {
  if (input.visitsShownNext7d < 1) return;
  if (safeLocalStorage.getItem(firstWeekVisibleStorageKey(input.orgId)) === 'true') return;

  analytics.trackEvent(
    PostImportEvents.firstWeekVisible({
      org_id: input.orgId,
      role: input.role ?? undefined,
      visits_shown_next_7d: input.visitsShownNext7d,
      empty_active_services: input.emptyActiveServices,
    }),
  );
  safeLocalStorage.setItem(firstWeekVisibleStorageKey(input.orgId), 'true');
  void updateOrganizationSettings({
    firstRun: { firstWeekVisibleAt: new Date().toISOString() },
  }).catch(() => {
    // Analytics stamp is best-effort — the local once-guard already held.
  });
}

export function fireScheduleEmptyStateShown(input: {
  orgId: string;
  role?: string | null;
  serviceId: string;
  serviceType: string;
  isActive: boolean;
  isEmpty: boolean;
}): void {
  if (!input.isActive || !input.isEmpty || !input.serviceId) return;
  const key = emptyStateStorageKey(input.orgId, input.serviceId);
  if (safeLocalStorage.getItem(key) === 'true') return;

  analytics.trackEvent(
    PostImportEvents.scheduleEmptyStateShown({
      org_id: input.orgId,
      role: input.role ?? undefined,
      service_id: input.serviceId,
      service_type: input.serviceType,
    }),
  );
  safeLocalStorage.setItem(key, 'true');
}

export function fireSetupModalEvent(
  kind: 'shown' | 'item_clicked' | 'dismissed' | 'completed',
  input: {
    orgId: string;
    role?: string | null;
    item: SetupModalItem;
    forks: FirstRunForks | null;
  },
): boolean {
  if (!input.forks || !isSetupModalItemVisible(input.item, input.forks)) return false;

  if (kind === 'shown') {
    const key = setupModalShownStorageKey(input.orgId, input.item);
    if (safeLocalStorage.getItem(key) === 'true') return false;
    analytics.trackEvent(
      PostImportEvents.setupModalShown({
        org_id: input.orgId,
        role: input.role ?? undefined,
        item: input.item,
      }),
    );
    safeLocalStorage.setItem(key, 'true');
    return true;
  }

  const generator = {
    item_clicked: PostImportEvents.setupModalItemClicked,
    dismissed: PostImportEvents.setupModalDismissed,
    completed: PostImportEvents.setupModalCompleted,
  }[kind];

  analytics.trackEvent(
    generator({
      org_id: input.orgId,
      role: input.role ?? undefined,
      item: input.item,
    }),
  );
  return true;
}

/** Persist the three fork answers. Fires server `import_completed` once execute has stored metrics. */
export async function saveFirstRunForks(forks: FirstRunForks): Promise<void> {
  await updateOrganizationSettings({
    firstRun: {
      hasSubcontractors: forks.hasSubcontractors,
      hasCrews: forks.hasCrews,
      payPath: forks.payPath,
    },
  });
}
